// 最小ルーティング: login / mfa-enroll / dashboard / tenant-admin / sf-audit を描画する。
// 認可の正本はサーバ側（設計 §8.3）。本ルーティングは UX のための遷移制御のみ。
// 認証後の画面は共通アプリシェル（左サイドバー）配下に統一する。
import "./styles.css";
import { config } from "./config";
import { createApiClient } from "./api/client";
import { renderLogin } from "./auth/login";
import { renderMfaEnroll } from "./auth/mfa-enroll";
import { renderDashboard } from "./dashboard/dashboard";
import { renderTenantAdmin } from "./tenant/tenant-admin";
import { renderSfAudit } from "./salesforce-audit/sf-audit";
import { renderGwAudit } from "./google-workspace-audit/gw-audit";
import { GW_SECTIONS, type GwSection } from "./google-workspace-audit/sections";
import { SF_SECTIONS, type SfSection, type AssistantContext } from "./salesforce-audit/sections";
import { createAiAssistant } from "./salesforce-audit/ai-assistant";
import { analyze as aiAnalyze } from "./salesforce-audit/ai-api";
import type { FindingsSummaryDto } from "./salesforce-audit/ai-api";
import { createAppShell, type ShellRoute } from "./layout/app-shell";
import { ensureToastContainer } from "./ui/toast";

const ACCESS_TOKEN_KEY = "cc.accessToken";

function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}
function setAccessToken(token: string): void {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
}
function clearAccessToken(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
}

let mfaSetupSession: string | undefined;
let mfaSetupUsername: string | undefined;

type Route = "login" | "mfa-enroll" | "dashboard" | "tenant-admin" | "sf-audit" | "gw-audit";

const SHELL_TITLES: Record<ShellRoute, string> = {
  dashboard: "ダッシュボード",
  "sf-audit": "Salesforce 監査",
  "gw-audit": "Google Workspace 監査",
  "tenant-admin": "テナント管理",
};

function currentRoute(): Route {
  const hash = window.location.hash.replace(/^#\/?/, "");
  if (hash === "mfa-enroll") return "mfa-enroll";
  if (hash === "tenant-admin") return "tenant-admin";
  if (hash === "login") return "login";
  if (hash === "sf-audit" || hash.startsWith("sf-audit/")) return "sf-audit";
  if (hash === "gw-audit" || hash.startsWith("gw-audit/")) return "gw-audit";
  return "dashboard";
}

function currentSfSection(): SfSection {
  const hash = window.location.hash.replace(/^#\/?/, "");
  const sub = hash.split("/")[1] ?? "summary";
  return (SF_SECTIONS as readonly string[]).includes(sub) ? (sub as SfSection) : "summary";
}

function currentGwSection(): GwSection {
  const hash = window.location.hash.replace(/^#\/?/, "");
  const sub = hash.split("/")[1] ?? "summary";
  return (GW_SECTIONS as readonly string[]).includes(sub) ? (sub as GwSection) : "summary";
}

function navigate(route: Route): void {
  const target = "#/" + route;
  if (window.location.hash === target) {
    mount();
  } else {
    window.location.hash = target;
  }
}

function navigateSf(section: SfSection): void {
  const target = section === "summary" ? "#/sf-audit" : "#/sf-audit/" + section;
  if (window.location.hash === target) {
    mount();
  } else {
    window.location.hash = target;
  }
}

function navigateGw(section: GwSection): void {
  const target = section === "summary" ? "#/gw-audit" : "#/gw-audit/" + section;
  if (window.location.hash === target) {
    mount();
  } else {
    window.location.hash = target;
  }
}

function logout(): void {
  clearAccessToken();
  mfaSetupSession = undefined;
  mfaSetupUsername = undefined;
  navigate("login");
}

// login / mfa-enroll は中央寄せの狭幅カードで表示する（#main は全幅。全幅シェルが潰れないよう
// カード幅の制約をここに閉じ込める, index.html 参照）。
function renderAuthScreen(root: HTMLElement, renderPage: (card: HTMLElement) => void): void {
  const card = document.createElement("div");
  card.className = "mx-auto max-w-md px-4 py-8";
  root.append(card);
  renderPage(card);
}

function renderInShell(
  root: HTMLElement,
  active: ShellRoute,
  renderPage: (content: HTMLElement) => void,
  headerActions?: readonly HTMLElement[],
): void {
  const { shell, content } = createAppShell({
    active,
    title: SHELL_TITLES[active],
    onNavigate: (route) => navigate(route),
    onLogout: logout,
    ...(headerActions ? { headerActions } : {}),
  });
  root.append(shell);
  renderPage(content);
}

// ヘッダ右上の AI アシスタント起動ボタン（絵文字回避: インライン SVG アイコン）。
function createAiButton(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className =
    "inline-flex items-center gap-1.5 rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100";
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M11 2 8.6 8.6 2 11l6.6 2.4L11 20l2.4-6.6L20 11l-6.6-2.4z");
  svg.append(path);
  const label = document.createElement("span");
  label.textContent = "AIアシスタント";
  btn.append(svg, label);
  btn.addEventListener("click", onClick);
  return btn;
}

function mount(): void {
  const root = document.getElementById("main");
  ensureToastContainer();
  if (root === null) {
    throw new Error("Root element #main not found");
  }
  root.replaceChildren();

  const apiClient = createApiClient({
    baseUrl: config.apiBaseUrl,
    getAccessToken,
    onUnauthorized: () => {
      clearAccessToken();
      navigate("login");
    },
  });

  const hasToken = getAccessToken() !== null;
  const inMfaSetup = mfaSetupSession !== undefined && mfaSetupSession !== "";
  const current = currentRoute();
  const route: Route = hasToken
    ? current === "login"
      ? "dashboard"
      : current
    : inMfaSetup && current === "mfa-enroll"
      ? "mfa-enroll"
      : "login";

  if (route === "login") {
    renderAuthScreen(root, (card) => {
      renderLogin(card, {
        config,
        onAuthenticated: (accessToken) => {
          setAccessToken(accessToken);
          navigate("dashboard");
        },
        onMfaSetupRequired: (session, username) => {
          mfaSetupSession = session;
          mfaSetupUsername = username;
          navigate("mfa-enroll");
        },
      });
    });
    return;
  }

  if (route === "mfa-enroll") {
    if (mfaSetupSession !== undefined && mfaSetupSession !== "") {
      const capturedSession = mfaSetupSession;
      const capturedUsername = mfaSetupUsername;
      renderAuthScreen(root, (card) => {
        renderMfaEnroll(card, {
          config,
          getAccessToken,
          onDone: () => navigate("dashboard"),
          session: capturedSession,
          ...(capturedUsername !== undefined ? { username: capturedUsername } : {}),
          onAuthenticated: (token) => {
            mfaSetupSession = undefined;
            mfaSetupUsername = undefined;
            setAccessToken(token);
            navigate("dashboard");
          },
        });
      });
    } else {
      renderAuthScreen(root, (card) => {
        renderMfaEnroll(card, {
          config,
          getAccessToken,
          onDone: () => navigate("dashboard"),
        });
      });
    }
    return;
  }

  if (route === "dashboard") {
    renderInShell(root, "dashboard", (content) => {
      renderDashboard(content, { apiClient, onNavigate: (r) => navigate(r) });
    });
    return;
  }

  if (route === "sf-audit") {
    const assistant = createAiAssistant({ analyze: (summary, messages) => aiAnalyze({ apiClient }, { summary, messages }) });
    const aiBtn = createAiButton(() => assistant.open());
    renderInShell(
      root,
      "sf-audit",
      (content) => {
        renderSfAudit(content, {
          section: currentSfSection(),
          onNavigate: (s) => navigateSf(s),
          openAssistant: (ctx?: AssistantContext, summary?: FindingsSummaryDto) => assistant.open(ctx, summary),
          // 認証済み ApiClient を渡し、接続済みなら /sf/connection + /sf/audit/run の
          // 実データ経路を有効化する（未注入だと常にモック表示になる, SP2 配線）。
          apiClient,
        });
        content.append(assistant.element);
      },
      [aiBtn],
    );
    return;
  }

  // Google Workspace 監査（GW-SP2）: サマリ/検出/接続のタブ枠を描画する。
  // #/gw-audit=サマリ、#/gw-audit/findings=検出、#/gw-audit/connect=接続。
  if (route === "gw-audit") {
    renderInShell(root, "gw-audit", (content) => {
      renderGwAudit(content, { section: currentGwSection(), onNavigate: (s) => navigateGw(s), apiClient });
    });
    return;
  }

  renderInShell(root, "tenant-admin", (content) => {
    renderTenantAdmin(content, { apiClient });
  });
}

window.addEventListener("hashchange", mount);
window.addEventListener("DOMContentLoaded", mount);
